import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError } from "@/api/client";
import {
  createUnit,
  deleteUnit,
  getUnit,
  getUnitCapacityTimeline,
  getUnitLoad,
  getUnitLoadByProject,
  listUnits,
  listUsersLookup,
  moveUnit,
  patchUnit,
  setUnitApprovalMode,
  type Unit,
  type UnitApprovalMode,
  type UnitDetail,
  type UnitCapacityTimelineDay,
  type UnitCreateRequest,
  type UnitDateRange,
  type UnitLoadByProjectDay,
  type UnitLoadDay,
  type UnitMoveRequest,
  type UnitPatchRequest,
  type UserLookup,
  type UserLookupPurpose,
  type UserRole,
} from "@/api/units";
import {
  listProjects,
  listProjectTasks,
  type ProjectListItem,
  type TaskWithCritical,
} from "@/api/projects";
import {
  PROJECTS_PORTFOLIO_KEY,
  PROJECTS_PORTFOLIO_PARAMS,
  PROJECT_TASKS_KEY,
} from "./useProjects";

export const UNITS_KEY = ["units"] as const;
export const UNIT_KEY = (id: number) => ["unit", id] as const;
export const UNIT_LOAD_KEY = (id: number, range: UnitDateRange) =>
  ["unit-load", id, range] as const;
export const UNIT_LOAD_BY_PROJECT_KEY = (id: number, range: UnitDateRange) =>
  ["unit-load-by-project", id, range] as const;
export const UNIT_CAPACITY_KEY = (id: number, range: UnitDateRange, subtree: boolean) =>
  ["unit-capacity", id, range, subtree] as const;

function noRetryOn401(failureCount: number, error: Error): boolean {
  if (error instanceof ApiError && error.status === 401) return false;
  return failureCount < 1;
}

export function useUnits(options: { refetchOnMount?: boolean | "always" } = {}) {
  return useQuery<Unit[], Error>({
    queryKey: UNITS_KEY,
    queryFn: () => listUnits(),
    retry: noRetryOn401,
    staleTime: 60_000,
    refetchOnMount: options.refetchOnMount,
  });
}

export function useUnit(id: number) {
  return useQuery<UnitDetail, Error>({
    queryKey: UNIT_KEY(id),
    queryFn: () => getUnit(id),
    enabled: id > 0,
    retry: noRetryOn401,
    staleTime: 60_000,
  });
}

export function useUnitLoad(id: number, range: UnitDateRange, enabled = true) {
  return useQuery<UnitLoadDay[], Error>({
    queryKey: UNIT_LOAD_KEY(id, range),
    queryFn: () => getUnitLoad(id, range),
    enabled: enabled && id > 0,
    retry: noRetryOn401,
    staleTime: 30_000,
  });
}

export function useUnitLoadByProject(id: number, range: UnitDateRange, enabled = true) {
  return useQuery<UnitLoadByProjectDay[], Error>({
    queryKey: UNIT_LOAD_BY_PROJECT_KEY(id, range),
    queryFn: () => getUnitLoadByProject(id, range),
    enabled: enabled && id > 0,
    retry: noRetryOn401,
    staleTime: 30_000,
  });
}

export function useUnitCapacityTimeline(
  id: number,
  range: UnitDateRange,
  enabled = true,
  subtree = false,
) {
  return useQuery<UnitCapacityTimelineDay[], Error>({
    queryKey: UNIT_CAPACITY_KEY(id, range, subtree),
    queryFn: () => getUnitCapacityTimeline(id, range, subtree),
    enabled: enabled && id > 0,
    retry: noRetryOn401,
    staleTime: 60_000,
  });
}

export function useCreateUnit() {
  const qc = useQueryClient();
  return useMutation<Unit, Error, UnitCreateRequest>({
    mutationFn: createUnit,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: UNITS_KEY });
      void qc.invalidateQueries({ queryKey: ["people-organization"] });
    },
  });
}

export interface PatchUnitVars {
  id: number;
  body: UnitPatchRequest;
}

export function usePatchUnit() {
  const qc = useQueryClient();
  return useMutation<Unit, Error, PatchUnitVars>({
    mutationFn: ({ id, body }) => patchUnit(id, body),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: UNITS_KEY });
      void qc.invalidateQueries({ queryKey: ["people-organization"] });
      void qc.invalidateQueries({ queryKey: UNIT_KEY(vars.id) });
    },
  });
}

export interface MoveUnitVars {
  id: number;
  body: UnitMoveRequest;
}

export function useMoveUnit() {
  const qc = useQueryClient();
  return useMutation<Unit, Error, MoveUnitVars>({
    mutationFn: ({ id, body }) => moveUnit(id, body),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: UNITS_KEY });
      void qc.invalidateQueries({ queryKey: ["people-organization"] });
      void qc.invalidateQueries({ queryKey: UNIT_KEY(vars.id) });
    },
  });
}

export function useSetApprovalMode(unitId: number) {
  const qc = useQueryClient();
  return useMutation<Unit, Error, UnitApprovalMode>({
    mutationFn: (mode) => setUnitApprovalMode(unitId, mode),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: UNITS_KEY });
      void qc.invalidateQueries({ queryKey: ["people-organization"] });
      void qc.invalidateQueries({ queryKey: UNIT_KEY(unitId) });
    },
  });
}

export function useDeleteUnit() {
  const qc = useQueryClient();
  return useMutation<void, Error, number>({
    mutationFn: (id) => deleteUnit(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: UNITS_KEY });
      void qc.invalidateQueries({ queryKey: ["people-organization"] });
    },
  });
}

/**
 * Candidates for picker UIs backed by `/users/lookup`.
 *
 * `role` narrows the list to a single role, while `purpose` selects a
 * server-owned domain policy. Omitting both keeps the shared endpoint's
 * all-active default. Filters are part of the query key so variants cache
 * independently.
 */
export function useUsersLookup(
  opts: { role?: UserRole; purpose?: UserLookupPurpose; enabled?: boolean } = {},
) {
  const { role, purpose, enabled = true } = opts;
  return useQuery<UserLookup[], Error>({
    queryKey: ["users-lookup", role ?? null, purpose ?? null],
    queryFn: () => listUsersLookup({ role, purpose }),
    enabled,
    retry: noRetryOn401,
    staleTime: 60_000,
  });
}

export interface PortfolioData {
  projects: ProjectListItem[];
  tasksByProject: Map<number, TaskWithCritical[]>;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
}

export function usePortfolio(): PortfolioData {
  const projectsQuery = useQuery<ProjectListItem[], Error>({
    queryKey: PROJECTS_PORTFOLIO_KEY,
    queryFn: () => listProjects(PROJECTS_PORTFOLIO_PARAMS),
    retry: noRetryOn401,
    staleTime: 30_000,
  });
  const projects = projectsQuery.data ?? [];

  const taskQueries = useQueries({
    queries: projects.map((p) => ({
      queryKey: PROJECT_TASKS_KEY(p.id ?? -1),
      queryFn: () => listProjectTasks(p.id ?? -1),
      enabled: typeof p.id === "number",
      retry: noRetryOn401,
      staleTime: 30_000,
    })),
  });

  const tasksByProject = new Map<number, TaskWithCritical[]>();
  projects.forEach((p, idx) => {
    if (typeof p.id !== "number") return;
    const data = taskQueries[idx]?.data;
    if (data) tasksByProject.set(p.id, data);
  });

  const isLoading = projectsQuery.isLoading || taskQueries.some((q) => q.isLoading);
  const isError = projectsQuery.isError || taskQueries.some((q) => q.isError);
  const error = projectsQuery.error ?? taskQueries.find((query) => query.error)?.error ?? null;

  return { projects, tasksByProject, isLoading, isError, error };
}
